import { useEffect, useRef } from 'react';
import type { NostrEvent } from '@nostrify/nostrify';
import { NoteCard } from '@/components/note';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

export function FeedSkeleton() {
  return (
    <div className="flex flex-col" aria-label="Loading feed">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="flex gap-3 border-b p-4">
          <Skeleton className="h-10 w-10 shrink-0 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-4/5" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function EmptyFeed({ message, action }: { message: string; action?: React.ReactNode }) {
  return (
    <Card className="m-4 border-dashed shadow-none">
      <CardContent className="px-8 py-12 text-center">
        <p className="mx-auto max-w-sm text-muted-foreground">{message}</p>
        {action && <div className="mt-4">{action}</div>}
      </CardContent>
    </Card>
  );
}

export function InfiniteFeed({
  events,
  isLoading,
  isFetchingNextPage,
  hasNextPage,
  fetchNextPage,
  emptyMessage = 'No notes yet. Try another relay set or check your connection.',
}: {
  events: NostrEvent[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage?: boolean;
  fetchNextPage: () => void;
  emptyMessage?: string;
}) {
  const sentinel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) fetchNextPage();
      },
      { rootMargin: '600px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (isLoading) return <FeedSkeleton />;
  if (events.length === 0) return <EmptyFeed message={emptyMessage} />;

  return (
    <div>
      {events.map((e) => (
        <NoteCard key={e.id} event={e} />
      ))}
      <div ref={sentinel} className="pb-20 md:pb-8">
        {isFetchingNextPage && <FeedSkeleton />}
        {!hasNextPage && events.length > 0 && (
          <p className="py-6 text-center text-sm text-muted-foreground">You're all caught up ✨</p>
        )}
        {hasNextPage && !isFetchingNextPage && (
          <div className="flex justify-center py-4">
            <Button variant="outline" size="sm" onClick={() => fetchNextPage()}>Load more</Button>
          </div>
        )}
      </div>
    </div>
  );
}
